import { startRegistration } from '@simplewebauthn/browser';
import { useState } from 'react';
import { IconDevice, IconShield } from '../components/icons';
import { CopyButton, Field, Skeleton, useAsync, useToast } from '../components/ui';
import { api, DeviceItem, SessionItem } from '../lib/api';
import { useSession } from '../lib/session';
import { ago } from '../lib/format';
import { t } from '../lib/i18n';

interface SecurityStatus {
  totp_enabled: boolean;
  totp_enabled_at: string | null;
  recovery_codes_remaining: number;
}
interface Setup {
  secret: string;
  otpauth_uri: string;
  qr_svg: string;
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const text = codes.join('\n');
  return (
    <div className="stack">
      <div className="callout warn">{t('Сохраните коды в менеджере паролей. Каждый работает один раз и показывается только сейчас.')}</div>
      <pre className="code" style={{ columns: 2, fontSize: 15 }}>{text}</pre>
      <div className="row">
        <CopyButton value={text} label={t('Скопировать коды')} />
        <button
          className="btn"
          onClick={() => {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([`${t('MailOcean — коды восстановления')}\n\n${text}\n`], { type: 'text/plain' }));
            a.download = 'mailocean-recovery-codes.txt';
            a.click();
          }}
        >
          {t('Скачать .txt')}
        </button>
        <span className="spacer" />
        <button className="btn btn-primary" onClick={onDone}>{t('Я сохранил коды')}</button>
      </div>
    </div>
  );
}

interface Passkey {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

function PasskeysCard() {
  const toast = useToast();
  const { refresh } = useSession();
  const list = useAsync(() => api.get<Passkey[]>('/security/passkeys'));
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true);
    try {
      const { options, state } = await api.post<{ options: Parameters<typeof startRegistration>[0]['optionsJSON']; state: string }>('/security/passkeys/options');
      const response = await startRegistration({ optionsJSON: options });
      await api.post('/security/passkeys', { response, state, name: name || 'Passkey' });
      toast(t('Passkey добавлен 🔑'));
      setName('');
      list.reload();
      void refresh();
    } catch (err) {
      const n = (err as Error).name;
      if (n !== 'NotAllowedError' && n !== 'AbortError') toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad stack" style={{ maxWidth: 760, marginTop: 16 }}>
      <div className="row">
        <span style={{ fontSize: 24 }}>🔑</span>
        <div style={{ flex: 1 }}>
          <h3>Passkeys</h3>
          <p className="small muted">{t('Вход отпечатком, лицом или PIN устройства — без пароля и кода. Защищает от фишинга: ключ работает только на этом сайте.')}</p>
        </div>
      </div>
      {list.data?.length ? (
        <table className="table">
          <tbody>
            {list.data.map((p) => (
              <tr key={p.id}>
                <td><strong>{p.name}</strong></td>
                <td className="small muted">{t('добавлен {when}', { when: ago(p.created_at) })}</td>
                <td className="small muted">{p.last_used_at ? t('вход {when}', { when: ago(p.last_used_at) }) : t('ещё не использовался')}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={async () => {
                      if (!confirm(t('Удалить passkey «{name}»?', { name: p.name }))) return;
                      await api.del(`/security/passkeys/${p.id}`);
                      list.reload();
                      void refresh();
                    }}
                  >
                    {t('Удалить')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="small muted">{t('Пока ни одного passkey.')}</p>
      )}
      <div className="row wrap">
        <input className="input" style={{ maxWidth: 260 }} placeholder={t('Название, например «MacBook»')} value={name} onChange={(e) => setName(e.target.value)} />
        <button className="btn btn-primary" onClick={add} disabled={busy}>{busy ? t('Ждём устройство…') : t('Добавить passkey')}</button>
      </div>
    </div>
  );
}

export function SecurityPage() {
  const toast = useToast();
  const { me, refresh } = useSession();
  const status = useAsync(() => api.get<SecurityStatus>('/security'));
  const [setup, setSetup] = useState<Setup | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState('');

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setError('');
    try {
      return await fn();
    } catch (e) {
      setError((e as Error).message);
      setCode('');
      return undefined;
    }
  }

  const s = status.data;
  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Безопасность')}</h1>
          <p>{t('Двухфакторный вход защищает ключи, домены и команду, даже если пароль утёк.')}</p>
        </div>
      </div>
      {me?.workspace.require_2fa && !me.user.mfa_enabled && (
        <div className="callout warn" style={{ maxWidth: 760, marginBottom: 16 }}>
          {t('Пространство «{name}» требует второй фактор. Включите приложение-аутентификатор или добавьте passkey — после этого откроются остальные разделы.', { name: me.workspace.name })}
        </div>
      )}
      <div className="card card-pad stack" style={{ maxWidth: 760 }}>
        <div className="row">
          <IconShield size={26} color={s?.totp_enabled ? 'var(--kelp)' : 'var(--muted)'} />
          <div style={{ flex: 1 }}>
            <h3>{t('Приложение-аутентификатор (TOTP)')}</h3>
            <p className="small muted">{t('Google Authenticator, 1Password, Bitwarden, Яндекс Ключ — любое приложение с кодами на 30 секунд.')}</p>
          </div>
          {s && <span className={`pill ${s.totp_enabled ? 'ok' : 'pending'}`}>{s.totp_enabled ? t('включён') : t('выключен')}</span>}
        </div>

        {!s ? (
          <Skeleton h={80} />
        ) : codes ? (
          <RecoveryCodes codes={codes} onDone={() => (setCodes(null), status.reload())} />
        ) : s.totp_enabled ? (
          <>
            <p className="small muted">
              {t('Включён {when}. Осталось кодов восстановления:', { when: ago(s.totp_enabled_at) })} <b>{s.recovery_codes_remaining}</b>
              {s.recovery_codes_remaining < 3 && t(' — пора выпустить новые.')}
            </p>
            <Field label={t('Код из приложения или код восстановления')} error={error}>
              <input className="input input-mono" style={{ maxWidth: 260 }} value={code} onChange={(e) => setCode(e.target.value.trim())} autoComplete="one-time-code" />
            </Field>
            <div className="row">
              <button
                className="btn"
                disabled={code.length < 6}
                onClick={async () => {
                  const r = await run(() => api.post<{ recovery_codes: string[] }>('/security/recovery-codes', { code }));
                  if (r) setCodes(r.recovery_codes);
                }}
              >
                {t('Новые коды восстановления')}
              </button>
              <button
                className="btn btn-danger"
                disabled={code.length < 6}
                onClick={async () => {
                  if (!confirm(t('Выключить двухфакторный вход? Аккаунт останется защищён только паролем.'))) return;
                  if ((await run(() => api.post('/security/totp/disable', { code }))) !== undefined) {
                    toast(t('Двухфакторный вход выключен'));
                    setCode('');
                    status.reload();
                  }
                }}
              >
                {t('Выключить')}
              </button>
            </div>
          </>
        ) : !setup ? (
          <div>
            <button className="btn btn-primary" onClick={async () => setSetup((await run(() => api.post<Setup>('/security/totp/setup'))) ?? null)}>
              {t('Включить двухфакторный вход')}
            </button>
          </div>
        ) : (
          <div className="grid" style={{ gridTemplateColumns: '200px minmax(0, 1fr)', alignItems: 'start' }}>
            <img alt={t('QR-код для приложения-аутентификатора')} src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qr_svg)}`} style={{ width: 200, height: 200, background: '#fff', borderRadius: 12, padding: 8 }} />
            <div className="stack">
              <p>{t('1. Отсканируйте QR-код в приложении. Не получается — введите ключ вручную:')}</p>
              <div className="secret-box"><code>{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code><CopyButton value={setup.secret} small label="" /></div>
              <Field label={t('2. Введите шесть цифр из приложения')} error={error}>
                <input
                  className="input input-mono"
                  style={{ maxWidth: 200, fontSize: 20, letterSpacing: 6, textAlign: 'center' }}
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                />
              </Field>
              <div>
                <button
                  className="btn btn-primary"
                  disabled={code.length !== 6}
                  onClick={async () => {
                    const r = await run(() => api.post<{ recovery_codes: string[] }>('/security/totp/enable', { code }));
                    if (r) {
                      toast(t('Двухфакторный вход включён 🛡'));
                      setSetup(null);
                      setCode('');
                      setCodes(r.recovery_codes);
                      status.reload();
                      void refresh(); // lifts the workspace 2FA fence
                    }
                  }}
                >
                  {t('Подтвердить и включить')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <PasskeysCard />
      <SessionsCard />
    </>
  );
}

/** Where the account is signed in: browsers (cookie sessions) and installs of the mobile app (refresh families). */
function SessionsCard() {
  const toast = useToast();
  const list = useAsync(() => api.get<{ items: SessionItem[] }>('/security/sessions'));
  const devices = useAsync(() => api.get<{ items: DeviceItem[] }>('/devices'));
  const [busy, setBusy] = useState<string | null>(null);
  const others = (list.data?.items ?? []).filter((x) => !x.current).length;

  async function revoke(ref: string) {
    setBusy(ref);
    try {
      await api.del(`/security/sessions/${ref}`);
      toast(t('Сессия завершена'));
      list.reload();
      devices.reload();
    } catch (e) {
      toast((e as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h3>{t('Сессии и устройства')}</h3>
        {others > 0 && (
          <button
            className="btn btn-sm"
            disabled={busy === 'all'}
            onClick={async () => {
              if (!confirm(t('Завершить все сессии, кроме этой? На других устройствах придётся войти заново.'))) return;
              setBusy('all');
              try {
                const r = await api.post<{ revoked: number }>('/security/sessions/revoke-others');
                toast(t('Завершено сессий: {n}', { n: r.revoked }));
                list.reload();
                devices.reload();
              } finally {
                setBusy(null);
              }
            }}
          >
            {t('Выйти на других устройствах')}
          </button>
        )}
      </div>
      {!list.data ? (
        <div className="card-pad stack"><Skeleton /><Skeleton /></div>
      ) : (
        <div>
          {list.data.items.map((x) => (
            <div key={x.session_ref} className="session-row">
              <IconDevice size={20} color="var(--muted)" />
              <div className="grow">
                <div>
                  <b>{x.device ?? (x.client === 'mobile' ? t('Мобильное приложение') : t('Браузер'))}</b>
                  {x.current && <span className="pill ok" style={{ marginLeft: 8 }}>{t('это устройство')}</span>}
                </div>
                <div className="small muted">
                  {x.client === 'mobile' ? t('приложение') : t('браузер')}
                  {x.ip ? ` · ${x.ip}` : ''} {t('· вход {when}', { when: ago(x.created_at) })}
                  {x.last_seen_at ? ` ${t('· активность {when}', { when: ago(x.last_seen_at) })}` : ''}
                </div>
              </div>
              {!x.current && (
                <button className="btn btn-sm" disabled={busy === x.session_ref} onClick={() => revoke(x.session_ref)}>
                  {t('Завершить')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {!!devices.data?.items.length && (
        <div className="card-pad small muted">
          {t('Push-уведомления получают:')} {devices.data.items.map((d) => `${d.platform === 'ios' ? 'iOS' : 'Android'} ${d.model ?? ''} (${t('приложение')} ${d.app_version})`.replace('  ', ' ')).join(', ')}.
        </div>
      )}
    </div>
  );
}
